import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, retry, throwError } from 'rxjs';
import { Allocation } from '../models/allocation.model';
import { environment } from '../../environments/environment';

/**
 * Payload for the Team Allocation Excel export. Mirrors AllocationExportRequestDto.
 *
 * Rows are chosen by the grid, not re-queried by the server, so the file always matches
 * whatever filters are active on screen.
 */
export interface AllocationExportRequest {
  periodLabel: string;
  filters: { label: string; value: string }[];
  rows: AllocationExportRow[];
  /**
   * Whether the workbook should carry the per-employee Total / Available rows.
   *
   * False once the grid is narrowed to presales-only or main-only: a total over a slice is
   * not the employee's total, and "Available = 100 - Total" would show capacity they do not
   * have, because their other allocations are filtered out of the file.
   */
  includeSummaryRows: boolean;
}

export interface AllocationExportRow {
  employeeId: string;
  employeeName: string;
  roleTitle: string;
  designationName: string;
  categoryName: string;
  managerName: string;
  invoiceTypeName: string;
  costCenterName: string;
  serviceGroupName: string;
  projectName: string;
  isProjectPresales: boolean;
  /** True when the row is a tracker activity twin rather than a pursuit. */
  isProjectPresalesActivity: boolean;
  weeks: number[];
  comments: string;
}

export interface CreateAllocationDto {
  employeeId: string;
  roleId: string;
  projectId: string;
  invoiceType: number;
  costCenterId?: string;
  serviceGroupId?: string;
  reportsToEmployeeId?: string;
  month: number;
  year: number;
  week1Percentage: number;
  week2Percentage: number;
  week3Percentage: number;
  week4Percentage: number;
  week5Percentage: number;
  comments?: string;
}

export interface UpdateAllocationDto {
  id: string;
  employeeId: string;
  roleId: string;
  projectId: string;
  invoiceType: number;
  costCenterId?: string;
  serviceGroupId?: string;
  reportsToEmployeeId?: string;
  month: number;
  year: number;
  week1Percentage: number;
  week2Percentage: number;
  week3Percentage: number;
  week4Percentage: number;
  week5Percentage: number;
  comments?: string;
}

export interface CopyRequestDto {
  month: number;
  year: number;
  createdBy?: string | null;
}

export interface CloseMonthRequestDto {
  month: number;
  year: number;
}

export interface EmployeeMonthlyAllocationSummary {
  employeeId: string;
  employeeName: string;
  month: number;
  year: number;
  totalWeek1: number;
  totalWeek2: number;
  totalWeek3: number;
  totalWeek4: number;
  totalWeek5: number;
  availableWeek1: number;
  availableWeek2: number;
  availableWeek3: number;
  availableWeek4: number;
  availableWeek5: number;
  allocationCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class AllocationService {
  private apiUrl = `${environment.apiUrl}/Allocation`;

  constructor(private http: HttpClient) { }

  getAllAllocations(year: number, month: number): Observable<Allocation[]> {
    // Add error handling with retry logic
    return this.http.get<Allocation[]>(`${this.apiUrl}/${year}/${month}`).pipe(
      retry(2),
      catchError(error => {
        console.error('Failed to fetch allocations:', error);
        return throwError(() => new Error('Failed to load allocations. Please try again.'));
      })
    );
  }

  /** Allocations for one project in a month — backs the read-only Team Allocation tab. */
  getProjectAllocations(projectId: string, year: number, month: number): Observable<Allocation[]> {
    return this.http.get<Allocation[]>(`${this.apiUrl}/project/${projectId}/${year}/${month}`).pipe(
      retry(2),
      catchError(error => {
        console.error('Failed to fetch project allocations:', error);
        return throwError(() => new Error('Failed to load team allocation. Please try again.'));
      })
    );
  }

  createAllocation(allocation: CreateAllocationDto): Observable<Allocation> {
    return this.http.post<Allocation>(this.apiUrl, allocation).pipe(
      catchError(error => this.handleError(error, 'Failed to create allocation'))
    );
  }

  updateAllocation(id: string, allocation: Allocation): Observable<Allocation> {
    // Create a proper DTO that matches the backend AllocationDto
    // Convert empty strings to undefined for optional fields
    // Use optional chaining to safely handle null values
    const updateDto: UpdateAllocationDto = {
      id: allocation.id,
      employeeId: allocation.employeeId,
      roleId: allocation.roleId,
      projectId: allocation.projectId,
      invoiceType: allocation.invoiceType,
      costCenterId: allocation.costCenterId?.trim() !== '' ? allocation.costCenterId : undefined,
      serviceGroupId: allocation.serviceGroupId?.trim() !== '' ? allocation.serviceGroupId : undefined,
      reportsToEmployeeId: allocation.reportsToEmployeeId?.trim() !== '' ? allocation.reportsToEmployeeId : undefined,
      month: allocation.month,
      year: allocation.year,
      week1Percentage: allocation.week1Percentage ?? 0,
      week2Percentage: allocation.week2Percentage ?? 0,
      week3Percentage: allocation.week3Percentage ?? 0,
      week4Percentage: allocation.week4Percentage ?? 0,
      week5Percentage: allocation.week5Percentage ?? 0,
      comments: allocation.comments?.trim() !== '' ? allocation.comments : undefined
    };
    
    return this.http.put<Allocation>(`${this.apiUrl}/${id}`, updateDto).pipe(
      catchError(error => this.handleError(error, 'Failed to update allocation'))
    );
  }

  deleteAllocation(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => this.handleError(error, 'Failed to delete allocation'))
    );
  }

  copyToNextMonth(request: CopyRequestDto): Observable<any> {
    return this.http.post(`${this.apiUrl}/copy-to-next-month`, request).pipe(
      catchError(error => {
        console.error('Failed to copy allocations to next month:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Posts the rows the grid is displaying and gets back the rendered workbook.
   *
   * The caller selects the rows so the file always matches the on-screen filters; the server
   * only renders, because ClosedXML can write the styling the browser-side xlsx build drops.
   */
  exportToExcel(request: AllocationExportRequest): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/export-excel`, request, {
      responseType: 'blob'
    }).pipe(
      catchError(error => this.handleError(error, 'Failed to export to Excel'))
    );
  }

  closeMonth(request: CloseMonthRequestDto): Observable<any> {
    return this.http.post(`${this.apiUrl}/close-month`, request).pipe(
      catchError(error => this.handleError(error, 'Failed to close month'))
    );
  }

  reopenMonth(request: CloseMonthRequestDto): Observable<any> {
    return this.http.post(`${this.apiUrl}/reopen-month`, request).pipe(
      catchError(error => this.handleError(error, 'Failed to reopen month'))
    );
  }

  getMonthStatus(year: number, month: number): Observable<{ month: number; year: number; isClosed: boolean }> {
    return this.http.get<{ month: number; year: number; isClosed: boolean }>(`${this.apiUrl}/month-status/${year}/${month}`).pipe(
      catchError(error => this.handleError(error, 'Failed to get month status'))
    );
  }

  getMonthlySummaries(year: number, month: number): Observable<EmployeeMonthlyAllocationSummary[]> {
    return this.http.get<EmployeeMonthlyAllocationSummary[]>(`${this.apiUrl}/summaries/${year}/${month}`).pipe(
      catchError(() => of([]))
    );
  }

  private handleError(error: any, message: string): Observable<never> {
    console.error(`${message}:`, error);
    return throwError(() => new Error(`${message}. Please try again.`));
  }
}


